/**
 * Cloudflare Turnstile, as the vocabulary both halves of an exchange share.
 *
 * A widget in a browser produces a token; the endpoint the browser is about to
 * call hands that token to Cloudflare's siteverify along with the secret, and
 * only then does the real work. The web client draws the widget
 * (web/src/platform/turnstile/useTurnstile.tsx), the backend verifies the
 * token (backend/src/platform/turnstile/server.ts), and this file is what
 * neither half may disagree about.
 *
 * IT IS NOT AN AUTHENTICATION CONCERN, which is why it does not live with the
 * auth code. The first endpoints protected happen to be auth's, because they
 * are the ones that cost money when abused, but nothing about verifying a
 * token knows what the request underneath it does. A public contact form or a
 * free-tier signup is guarded by the same middleware without pulling a
 * password hasher in with it.
 */
/**
 * Where the token travels: a request header, not a body field.
 *
 * Cloudflare's own idiom is a form field, because the widget writes a hidden
 * `<input name="cf-turnstile-response">` into the `<form>` it sits in and the
 * browser posts it. Nothing here posts a form. Every protected endpoint
 * takes a JSON body that a zod schema validates, and those schemas reject
 * properties they do not declare — so carrying the token in the body would mean
 * every schema growing a field it has no opinion about, and a middleware
 * reading (and therefore consuming) a body its handler has not parsed yet.
 *
 * A header costs none of that and is read before the body is touched. The name
 * is Cloudflare's so that anyone who has read their documentation recognises
 * it here.
 */
export const TURNSTILE_HEADER = "cf-turnstile-response";
/**
 * Long enough for any token Cloudflare issues, short enough that a megabyte of
 * junk is refused before it reaches the network.
 */
export const MAX_TOKEN_LENGTH = 2048;
/**
 * An action names the surface a token was produced for, and the server checks
 * it. Without that check a token minted by the widget on the sign-up page is
 * accepted by the password-reset endpoint, and one solved challenge unlocks
 * every protected route on the site.
 */
const ACTION_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;
export function isValidAction(action) {
    return ACTION_PATTERN.test(action);
}
